import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ConversationService } from "@/services/conversation/ConversationService";
import type { Conversation } from "@/types/conversation/conversation";

/** Handled state of a listened email or call, saved through conversations update. */
export const useListenedHandled = (conversation: Conversation) => {
  const queryClient = useQueryClient();
  const [handled, setHandled] = useState(Boolean(conversation.handled_at));
  const [savingHandled, setSavingHandled] = useState(false);

  const updateHandled = async (next: boolean) => {
    setSavingHandled(true);
    try {
      await ConversationService.setHandled(conversation.id, next);
      setHandled(next);
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update the conversation");
    } finally {
      setSavingHandled(false);
    }
  };

  /**
   * A reply sent outside Help Genie (the team's own inbox or phone) is
   * invisible to it, so opening one counts as handling it; Undo covers
   * changing your mind.
   */
  const markHandledOnTrust = async (description: string) => {
    if (handled) return;
    await updateHandled(true);
    toast("Marked handled", { description, action: { label: "Undo", onClick: () => void updateHandled(false) } });
  };

  return { handled, savingHandled, updateHandled, markHandledOnTrust };
};
